import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import { driversFixture, updateDriverFixture, removeDriverTenantFixture } from './fixture';

const enc = encodeURIComponent;

/**
 * Live-CORE liefert `{ name, displayName, configSchema, hasEnvFallback,
 * global:{enabled,…}, tenant:{enabled,configPreview,hasCredentials,notes} }`.
 * Die Karten-Komponente arbeitet historisch mit der Fixture-Shape
 * (`key/globalEnabled/tenantEnabled`). Wir mappen die Live-Antwort einmal hier,
 * damit die Komponente unverändert bleibt — und damit die Mutationen einen
 * echten Driver-Key (statt `undefined`) bekommen.
 */
function normalizeDriver(d) {
  return {
    ...d,
    key: d.name,                                   // Route-Param (war undefined)
    name: d.displayName || d.name,                 // Titel
    description: d.description ?? '',
    globalEnabled: !!d.global?.enabled,
    tenantEnabled: !!d.tenant?.enabled,
    hasCredentials: !!d.tenant?.hasCredentials,
    notes: d.tenant?.notes ?? '',
    configSchema: d.configSchema || [],
    lastSuccessAt: d.global?.lastSuccessAt ?? null,
    lastFailureAt: d.global?.lastFailureAt ?? null,
    lastError: d.global?.lastError ?? d.global?.disabledReason ?? null,
  };
}

export function useDrivers() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['drivers', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return driversFixture();
      }
      const res = await fetch('/v1/admin/drivers');
      return { ...res, drivers: (res?.drivers ?? []).map(normalizeDriver) };
    },
  });
}

/**
 * Eine `patch`-basierte Fassade über die drei echten CORE-Endpunkte:
 *   - { globalEnabled } → POST /drivers/:key/global          (admin)
 *   - { tenantEnabled } → POST /drivers/:key/tenant/toggle
 *   - { config, notes } → PUT  /drivers/:key/tenant          (enabled:true)
 */
export function useUpdateDriver() {
  return useApiMutation({
    live: (fetch, { key, patch }) => {
      if (patch?.globalEnabled !== undefined) {
        return fetch(`/v1/admin/drivers/${enc(key)}/global`, { method: 'POST', body: { enabled: patch.globalEnabled } });
      }
      if (patch?.tenantEnabled !== undefined) {
        return fetch(`/v1/admin/drivers/${enc(key)}/tenant/toggle`, { method: 'POST', body: { enabled: patch.tenantEnabled } });
      }
      if (patch?.config !== undefined) {
        return fetch(`/v1/admin/drivers/${enc(key)}/tenant`, {
          method: 'PUT',
          body: { enabled: true, config: patch.config, notes: patch.notes ?? undefined },
        });
      }
      return Promise.resolve({ ok: true });
    },
    fixture: ({ key, patch }) => updateDriverFixture(key, patch),
    invalidate: ['drivers'],
    successMessage: 'Driver-Konfiguration gespeichert.',
  });
}

export function useRemoveDriverFromTenant() {
  return useApiMutation({
    live: (fetch, { key }) =>
      fetch(`/v1/admin/drivers/${enc(key)}/tenant`, { method: 'DELETE' }),
    fixture: ({ key }) => removeDriverTenantFixture(key),
    invalidate: ['drivers'],
    successMessage: 'Driver aus dieser Brand entfernt.',
  });
}
